import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info } from "lucide-react";
import { getHoldingPricing, saveHoldingPricing } from "../../../../api/billing";
import { showToast } from "../../../../utils/showToast";
import { Button } from "../../../../components/ui/Button";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { apiErrorCode } from "../../cobro/utils/collectionReminders";
import { holdingCodeText, holdingErrorText } from "../../cobro/utils/holdings";
import { formatDate } from "../../cobro/utils/cobroHelpers";
import RateCardEditor from "../RateCardEditor";

/**
 * Tarifa propia del holding (microservice-billing, commerce-pricing-config/holdings/:id), con
 * el mismo editor que la tarifa de un contrato y su "vigente desde". Sin tarifa propia la nota
 * consolidada del holding queda bloqueada: no se cobra con la base del operador sin avisar.
 *
 * Cada guardado REEMPLAZA la tarifa entera y crea una versión nueva; la nota se cobra con la
 * versión vigente al fin de su período. Por eso cada guardado lleva `expectedPricingVersion`,
 * la versión sobre la que se editó (0 si no había tarifa): si otra sesión guardó antes, billing
 * responde 409 `pricing_version_conflict` en vez de pisar esa versión con contenido viejo. No
 * se recarga sola: el editor conserva los cambios y el operador recarga cuando quiera.
 *
 * @param {{ holdingId: string, onSaved?: (view: object) => void }} props
 *   `onSaved` recibe la vista guardada (la ficha relee con ella el checklist del cobro
 *   consolidado).
 */
export default function HoldingPricingSection({ holdingId, onSaved }) {
  const { t } = useTranslation();
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Otra sesión guardó una versión más nueva que la que se estaba editando.
  const [conflictText, setConflictText] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await getHoldingPricing(holdingId);
      setView(data?.data ?? null);
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [holdingId]);

  useEffect(() => {
    setView(null);
    load();
  }, [load]);

  const handleSubmit = async (body) => {
    try {
      const { data } = await saveHoldingPricing(holdingId, {
        ...body,
        expectedPricingVersion: view?.pricingVersion ?? 0,
      });
      setView(data?.data ?? null);
      setConflictText(null);
      onSaved?.(data?.data ?? null);
      return true;
    } catch (err) {
      if (apiErrorCode(err) === "pricing_version_conflict") {
        setConflictText(holdingCodeText(err, t));
        return false;
      }
      showToast(holdingErrorText(err, t, "No se pudieron guardar las tarifas"), "error");
      return false;
    }
  };

  const reload = async () => {
    setConflictText(null);
    await load();
  };

  if (loading && !view) {
    return (
      <div className="space-y-3" role="status" aria-live="polite">
        <span className="sr-only">{t("Cargando…")}</span>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (loadError && !view) {
    return (
      <div
        className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error"
        role="alert"
      >
        <span>{holdingErrorText(loadError, t, "No se pudo cargar la tarifa del holding.")}</span>
        <Button size="sm" variant="outline" onClick={load}>
          {t("Reintentar")}
        </Button>
      </div>
    );
  }

  if (!view) return null;

  const history = Array.isArray(view.pricingHistory) ? view.pricingHistory : [];

  return (
    <div className="space-y-4">
      {conflictText && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error"
          role="alert"
        >
          <span>{conflictText}</span>
          <Button size="sm" variant="outline" onClick={reload}>
            {t("Recargar")}
          </Button>
        </div>
      )}

      {!view.exists && (
        <p className="flex items-start gap-2 rounded-md bg-status-warning-soft p-3 text-sm text-status-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            {t("El holding todavía no tiene tarifa propia: su nota consolidada queda bloqueada hasta que la cargues.")}
          </span>
        </p>
      )}

      {view.exists && (
        <div className="space-y-1 text-xs text-fg-muted">
          <div>
            {view.effectiveVersionToday != null
              ? t("Rige hoy la versión v{{n}}.", { n: view.effectiveVersionToday })
              : t("Todavía no rige ninguna versión: la primera empieza en una fecha futura.")}
          </div>
          {history.length > 0 && (
            <ul aria-label={t("Versiones de la tarifa")} className="flex flex-wrap gap-x-4 gap-y-1">
              {history.map((h) => (
                <li key={h.version}>
                  {t("v{{n}} vigente desde {{date}}", { n: h.version, date: formatDate(h.effectiveFrom) })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Decisión del 2026-09-25: el checkout (cotización de envíos en la tienda) sigue con la
          tarifa del contrato de cada empresa. Se avisa para que la diferencia no sorprenda. */}
      <p className="flex items-start gap-2 text-xs text-fg-muted">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          {t("La cotización de envíos en la tienda de cada empresa sigue usando la tarifa de su propio contrato: esta tarifa solo se usa para la nota consolidada del holding.")}
        </span>
      </p>

      <RateCardEditor
        key={`${holdingId}:${view.pricingVersion}`}
        subject="holding"
        contract={{
          pricing: view.pricing ?? {},
          pricingVersion: view.exists ? view.pricingVersion : undefined,
          pricingHistory: history,
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
